import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import api from '../../../utils/api';
import { showToast } from '../../../Components/ToastNotifications';

// Versi mentor dari Pages/Admin/Attendances/AttendancesDetail.jsx — detail
// satu sesi absensi (bukan kalender bulanan seperti versi lama), dibatasi
// backend ke divisi yang dibimbing mentor ini saja.
const getStatusLabel = (status) => {
  switch (status) {
    case 'ON_TIME':
      return 'Hadir';
    case 'LATE_TOLERANCE':
      return 'Hadir (Toleransi)';
    case 'PENDING_APPROVAL':
      return 'Menunggu Persetujuan';
    case 'EARLY_EXIT_PENDING':
      return 'Keluar Lebih Awal';
    case 'ABSENT':
      return 'Belum Absen';
    case 'LATE':
      return 'Terlambat';
    default:
      return status || '-';
  }
};

const getStatusBadgeClass = (status) => {
  if (status === 'ON_TIME') return 'bg-success-subtle text-success';
  if (status === 'PENDING_APPROVAL') return 'bg-warning-subtle text-warning';
  if (status === 'LATE') return 'bg-danger-subtle text-danger';
  return 'bg-secondary-subtle text-secondary';
};

// clock_in_status/clock_out_status TIDAK berubah saat tolerance request
// ditolak (lihat CLPAdminUpdateToleranceStatusUseCase) -- sengaja dibiarkan
// PENDING_APPROVAL/EARLY_EXIT_PENDING supaya otomatis tidak dihitung hadir
// di skor. Makanya label tampilan harus dikombinasikan dengan status
// tolerance request terkait, bukan baca clock_in_status/clock_out_status
// begitu saja.
const getCombinedStatus = (rawStatus, requestType, toleranceRequests) => {
  const isPendingLike = rawStatus === 'PENDING_APPROVAL' || rawStatus === 'EARLY_EXIT_PENDING';
  if (!isPendingLike) {
    return { label: getStatusLabel(rawStatus), className: getStatusBadgeClass(rawStatus), hint: null };
  }

  const related = (toleranceRequests || []).find((r) => r.requestType === requestType);
  if (!related) {
    return {
      label: 'Menunggu Pengajuan Toleransi',
      className: 'bg-warning-subtle text-warning',
      hint: null,
    };
  }
  if (related.status === 'PENDING') {
    return {
      label: 'Menunggu Persetujuan',
      className: 'bg-warning-subtle text-warning',
      hint: 'Belum dihitung hadir sampai Anda tinjau pengajuan toleransinya di bawah.',
    };
  }
  if (related.status === 'REJECTED') {
    return {
      label: 'Ditolak',
      className: 'bg-danger-subtle text-danger',
      hint: 'Anda menolak pengajuan toleransinya, jadi sesi ini tidak dihitung sebagai hadir.',
    };
  }

  return { label: getStatusLabel(rawStatus), className: getStatusBadgeClass(rawStatus), hint: null };
};

const getRequestTypeLabel = (type) => {
  switch (type) {
    case 'LATE_CLOCK_IN':
      return 'Keterlambatan Clock In';
    case 'LATE_CLOCK_OUT':
      return 'Keterlambatan Clock Out';
    case 'MISSED_CLOCK_IN':
      return 'Lupa Clock In';
    case 'MISSED_CLOCK_OUT':
      return 'Lupa Clock Out';
    case 'EARLY_CLOCK_OUT':
      return 'Clock Out Lebih Awal';
    default:
      return type || '-';
  }
};

const getRequestTypeBadge = (type) => {
  if (type === 'LATE_CLOCK_IN') {
    return { className: 'bg-warning-subtle text-warning', icon: 'bi-clock-history' };
  }
  if (type === 'EARLY_CLOCK_OUT') {
    return { className: 'bg-info-subtle text-info', icon: 'bi-box-arrow-right' };
  }
  return { className: 'bg-secondary-subtle text-secondary', icon: 'bi-question-circle' };
};

const getToleranceStatusLabel = (status) => {
  if (status === 'APPROVED') return 'Disetujui';
  if (status === 'REJECTED') return 'Ditolak';
  return 'Menunggu Persetujuan';
};

const AttendancesByMentorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.getMentorAttendanceDetail(id);
      setData(response);
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Gagal mengambil data absensi', 'danger');
      navigate('/mentor/attendances');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleReviewTolerance = async (requestId, status) => {
    if (!requestId) return;
    try {
      setIsSubmitting(true);
      await api.reviewMentorTolerance(requestId, { status });
      showToast(
        status === 'APPROVED'
          ? 'Pengajuan toleransi disetujui. Mentee akan menerima notifikasi hasil ini.'
          : 'Pengajuan toleransi ditolak. Mentee akan menerima notifikasi hasil ini.',
        'success',
      );
      fetchDetail();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Gagal memproses pengajuan toleransi. Silakan coba lagi.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='text-center mt-5'>
        <div className='spinner-border text-primary' role='status'></div>
        <p className='mt-2 text-muted'>Memuat data...</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 p-4'>
      {/* Header Section */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <button
            className='btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center'
            style={{ width: '40px', height: '40px' }}
            onClick={() => navigate('/mentor/attendances')}
          >
            <i className='bi bi-arrow-left text-dark fs-5'></i>
          </button>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-calendar-check text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Detail Absensi Mentee</h1>
            <p className='text-muted mb-0 d-none d-lg-block' style={{ fontSize: '0.75rem' }}>
              Data rekaman kehadiran dan logbook.
            </p>
          </div>
        </div>
      </div>

      <div className='row g-4'>
        {/* Info Utama */}
        <div className='col-lg-4'>
          <div className='card border border-light-subtle rounded-4 h-100'>
            <div className='card-header bg-white border-0 p-4 pb-0'>
              <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                <i className='bi bi-person-badge text-primary'></i>
                Informasi Sesi
              </h6>
            </div>
            <div className='card-body p-4'>
              <div className='mb-3'>
                <label className='text-muted small fw-semibold d-block mb-1'>Mentee</label>
                <div className='bg-light p-3 rounded-4'>
                  <div className='d-flex justify-content-between align-items-center gap-2'>
                    <span className='fw-bold text-dark text-break small'>
                      {data.firstName || data.lastName
                        ? `${data.firstName || ''} ${data.lastName || ''}`.trim()
                        : `ID ${data.userId}`}
                    </span>
                    <button
                      onClick={() => navigate(`/mentor/attendances/participants/${data.userId}`, {
                        state: { firstName: data.firstName, lastName: data.lastName },
                      })}
                      className='btn btn-link btn-sm text-decoration-none p-0 fw-bold flex-shrink-0'
                      style={{ fontSize: '0.8rem' }}
                    >
                      Kuota &amp; Riwayat
                    </button>
                  </div>
                </div>
              </div>
              <div className='mb-3'>
                <span className='text-muted extra-small fw-bold text-uppercase d-block mb-1' style={{ fontSize: '0.65rem' }}>
                  Sesi Tanggal
                </span>
                <div className='fw-medium text-dark small'>
                  {data.sessionDate ? format(new Date(data.sessionDate), 'dd MMMM yyyy', { locale: localeId }) : '-'}
                </div>
                {!!data.totalBatchDays && (
                  <span className='badge bg-primary-subtle text-primary rounded-pill px-2 py-1 mt-2 fw-semibold extra-small'>
                    Hari ke-{data.dayIndex} dari {data.totalBatchDays} hari periode batch
                  </span>
                )}
              </div>
              <div className='mb-3'>
                <span className='text-muted extra-small fw-bold text-uppercase d-block mb-1' style={{ fontSize: '0.65rem' }}>
                  Status Masuk
                </span>
                <span className={`badge rounded-pill px-3 py-1 fw-semibold extra-small ${getCombinedStatus(data.clockInStatus, 'LATE_CLOCK_IN', data.toleranceRequest).className}`}>
                  {getCombinedStatus(data.clockInStatus, 'LATE_CLOCK_IN', data.toleranceRequest).label}
                </span>
                {getCombinedStatus(data.clockInStatus, 'LATE_CLOCK_IN', data.toleranceRequest).hint && (
                  <p className='text-muted mb-0 mt-1' style={{ fontSize: '0.7rem' }}>
                    {getCombinedStatus(data.clockInStatus, 'LATE_CLOCK_IN', data.toleranceRequest).hint}
                  </p>
                )}
              </div>
              <div>
                <span className='text-muted extra-small fw-bold text-uppercase d-block mb-1' style={{ fontSize: '0.65rem' }}>
                  Status Keluar
                </span>
                <span className={`badge rounded-pill px-3 py-1 fw-semibold extra-small ${getCombinedStatus(data.clockOutStatus, 'EARLY_CLOCK_OUT', data.toleranceRequest).className}`}>
                  {getCombinedStatus(data.clockOutStatus, 'EARLY_CLOCK_OUT', data.toleranceRequest).label}
                </span>
                {getCombinedStatus(data.clockOutStatus, 'EARLY_CLOCK_OUT', data.toleranceRequest).hint && (
                  <p className='text-muted mb-0 mt-1' style={{ fontSize: '0.7rem' }}>
                    {getCombinedStatus(data.clockOutStatus, 'EARLY_CLOCK_OUT', data.toleranceRequest).hint}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Absensi */}
        <div className='col-lg-8'>
          <div className='card border border-light-subtle rounded-4 mb-4'>
            <div className='card-header bg-white border-0 p-4 pb-0'>
              <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                <i className='bi bi-clock-history text-primary'></i>
                Rekaman Waktu
              </h6>
            </div>
            <div className='card-body p-4'>
              <div className='row g-3'>
                <div className='col-md-6'>
                  <div className='bg-light p-3 rounded-4'>
                    <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.65rem' }}>
                      Clock In
                    </span>
                    <h4 className='fw-bold mb-2 text-dark'>
                      {data.clockInAt ? format(new Date(data.clockInAt), 'HH:mm:ss') : '--:--'}
                    </h4>
                    {data.clockInLatitude && data.clockInLongitude && (
                      <a
                        href={`https://www.google.com/maps?q=${data.clockInLatitude},${data.clockInLongitude}`}
                        target='_blank'
                        rel='noreferrer'
                        className='small text-primary text-decoration-none d-inline-flex align-items-center'
                      >
                        <i className='bi bi-geo-alt-fill text-danger me-1'></i>
                        {Number(data.clockInLatitude).toFixed(4)}, {Number(data.clockInLongitude).toFixed(4)}
                        <i className='bi bi-box-arrow-up-right ms-2' style={{ fontSize: '0.7rem' }}></i>
                      </a>
                    )}
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='bg-light p-3 rounded-4'>
                    <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.65rem' }}>
                      Clock Out
                    </span>
                    <h4 className='fw-bold mb-2 text-dark'>
                      {data.clockOutAt ? format(new Date(data.clockOutAt), 'HH:mm:ss') : '--:--'}
                    </h4>
                    {data.clockOutLatitude && data.clockOutLongitude && (
                      <a
                        href={`https://www.google.com/maps?q=${data.clockOutLatitude},${data.clockOutLongitude}`}
                        target='_blank'
                        rel='noreferrer'
                        className='small text-primary text-decoration-none d-inline-flex align-items-center'
                      >
                        <i className='bi bi-geo-alt-fill text-danger me-1'></i>
                        {Number(data.clockOutLatitude).toFixed(4)}, {Number(data.clockOutLongitude).toFixed(4)}
                        <i className='bi bi-box-arrow-up-right ms-2' style={{ fontSize: '0.7rem' }}></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Toleransi */}
          {data.toleranceRequest && Array.isArray(data.toleranceRequest) && data.toleranceRequest.map((request) => (
            <div key={request.id} className='card border border-light-subtle rounded-4 mb-4 border-start border-warning border-4'>
              <div className='card-body p-4'>
                <div className='d-flex align-items-start justify-content-between mb-1'>
                  <div>
                    <h6 className='fw-bold mb-0 text-warning d-flex align-items-center gap-2'>
                      <i className='bi bi-exclamation-triangle-fill'></i>
                      Pengajuan Toleransi
                    </h6>
                    {request.status === 'PENDING' && (
                      <p className='text-muted mb-0 mt-1' style={{ fontSize: '0.75rem' }}>
                        Mentee ini mengajukan keringanan untuk sesi kehadirannya. Tinjau alasannya sebelum menyetujui atau menolak — <strong>Setujui</strong> akan menghitung sesi ini sebagai hadir, <strong>Tolak</strong> tidak akan menghitungnya.
                      </p>
                    )}
                  </div>
                  <span
                    className={`badge rounded-pill px-3 py-1 fw-semibold extra-small flex-shrink-0 ${request.status === 'APPROVED' ? 'bg-success-subtle text-success' : request.status === 'REJECTED' ? 'bg-danger-subtle text-danger' : 'bg-warning-subtle text-warning'}`}
                  >
                    {getToleranceStatusLabel(request.status)}
                  </span>
                </div>
                <div className='row mb-2 mt-2'>
                  <div className='col-md-6'>
                    <label className='text-muted small fw-semibold d-block mb-1'>Jenis Keterlambatan</label>
                    <span
                      className={`badge rounded-pill px-3 py-1 fw-semibold extra-small d-inline-flex align-items-center gap-1 ${getRequestTypeBadge(request.requestType).className}`}
                    >
                      <i className={`bi ${getRequestTypeBadge(request.requestType).icon}`}></i>
                      {getRequestTypeLabel(request.requestType)}
                    </span>
                  </div>
                  <div className='col-md-6'>
                    <label className='text-muted small fw-semibold'>Diajukan Pada</label>
                    <p className='fw-medium mb-2 small'>{format(new Date(request.requestedAt), 'dd MMM yyyy, HH:mm')}</p>
                  </div>
                  <div className='col-12 mt-2'>
                    <label className='text-muted small fw-semibold'>Alasan dari Mentee</label>
                    <div className='bg-light p-3 rounded-4 mt-1 text-dark small'>{request.reason}</div>
                  </div>
                  {request.status !== 'PENDING' && request.reviewerName && (
                    <div className='col-12 mt-3'>
                      <div className='d-flex align-items-center gap-2 text-muted small'>
                        <i className='bi bi-person-check-fill'></i>
                        {request.status === 'APPROVED' ? 'Disetujui' : 'Ditolak'} oleh <span className='fw-semibold text-dark'>{request.reviewerName}</span>
                        {request.reviewedAt && (
                          <span>· {format(new Date(request.reviewedAt), 'dd MMM yyyy, HH:mm')}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {request.status === 'PENDING' && (
                  <div className='d-flex flex-column flex-sm-row align-items-sm-center justify-content-end gap-2 mt-3'>
                    <span className='text-muted me-auto d-none d-sm-inline' style={{ fontSize: '0.7rem' }}>
                      Keputusan akan langsung diberitahukan ke mentee.
                    </span>
                    <button
                      className='btn btn-outline-danger btn-sm px-4 rounded-pill fw-bold'
                      disabled={isSubmitting}
                      onClick={() => handleReviewTolerance(request.id, 'REJECTED')}
                    >
                      Tolak Pengajuan
                    </button>
                    <button
                      className='btn btn-success btn-sm px-4 rounded-pill fw-bold shadow-sm'
                      disabled={isSubmitting}
                      onClick={() => handleReviewTolerance(request.id, 'APPROVED')}
                    >
                      Setujui Pengajuan
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Logbook */}
          {data.hasLogbook ? (
            <div className='card border border-light-subtle rounded-4 overflow-hidden'>
              <div className='card-header bg-white border-0 p-4 pb-0'>
                <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                  <i className='bi bi-journal-text text-primary'></i>
                  Logbook Harian
                </h6>
              </div>
              <div className='card-body p-4'>
                <div className='mb-4'>
                  <label className='fw-bold mb-2 small'>1. Progress Hari Ini</label>
                  <div className='bg-light p-3 rounded-4'>
                    <ol className='mb-0 ps-3 small'>
                      {data.progress?.split(',')?.map((item, i) => <li key={i}>{item.trim()}</li>)}
                    </ol>
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='fw-bold mb-2 small'>2. Rencana Tindak Lanjut</label>
                  <div className='bg-light p-3 rounded-4'>
                    <ol className='mb-0 ps-3 small'>
                      {data.nextSteps?.split(',')?.map((item, i) => <li key={i}>{item.trim()}</li>)}
                    </ol>
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='fw-bold mb-2 small'>3. Kendala</label>
                  <div className='bg-light p-3 rounded-4'>
                    <ol className='mb-0 ps-3 small'>
                      {data.challenges?.split(',')?.map((item, i) => <li key={i}>{item.trim()}</li>)}
                    </ol>
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='fw-bold mb-2 small'>4. Dukungan yang Dibutuhkan</label>
                  <div className='bg-light p-3 rounded-4'>
                    <ol className='mb-0 ps-3 small'>
                      {data.supportNeeded?.split(',')?.map((item, i) => <li key={i}>{item.trim()}</li>)}
                    </ol>
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='fw-bold mb-2 small'>5. Mood / Perasaan</label>
                  <div>
                    <span className='badge bg-primary-subtle text-primary px-3 py-1 rounded-pill fw-semibold extra-small'>{data.mood}</span>
                  </div>
                </div>
                <div>
                  <label className='fw-bold mb-2 small'>6. Refleksi Harian</label>
                  <div className='d-flex flex-wrap gap-2'>
                    {(Array.isArray(data.dailyReflection) ? data.dailyReflection : data.dailyReflection?.split(',') || []).map((tag, i) => (
                      <span key={i} className='badge bg-info-subtle text-info px-3 py-1 rounded-pill fw-semibold extra-small'>
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='card border border-light-subtle rounded-4 text-center py-5'>
              <div className='text-muted mb-2 opacity-25'>
                <i className='bi bi-journal-x fs-1'></i>
              </div>
              <h6 className='fw-bold text-dark'>Belum Mengisi Logbook</h6>
              <p className='small text-muted mb-0'>Mentee belum mengirimkan logbook untuk sesi ini.</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
      `}</style>
    </div>
  );
};

export default AttendancesByMentorDetail;
